import { Component, inject, model, ChangeDetectionStrategy, input, output, OnInit } from '@angular/core';
import { CurrencyPipe, NgClass } from '@angular/common';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  evenPercentageSplit,
  LookupStore,
  LookupViewModel,
  PAYROLL_PER_FIELD_OWNER,
  WizardClassViewModel,
  WizardStore,
} from '@program-suite/data-common';
import { ButtonComponent, UI_FORM_IMPORTS } from '@program-suite/ui';
import { AuthStore } from '@program-suite/data-auth';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { IconComponent } from '@program-suite/util-icons';
import { MatError, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';

@Component({
  selector: 'lib-coverage-adjustment-step1',
  imports: [CommonModule, ...UI_FORM_IMPORTS, ReactiveFormsModule, MatCheckboxModule, IconComponent, ButtonComponent, CurrencyPipe, MatError, MatFormFieldModule, MatInputModule ],
  templateUrl: './coverage-adjustment-step1.component.html',
  styleUrl: './coverage-adjustment-step1.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoverageAdjustmentStep1Component implements OnInit {
  form = model<FormGroup>();
  lookupStore = inject(LookupStore);
  authStore = inject(AuthStore);
  wizardStore = inject(WizardStore);
  displayGenerateQuote = input<boolean>(false);
  private _fb = inject(FormBuilder);
  private router = inject(Router);
  quoteRequested = output();
  data: any;

  // 1-10 owners active in the field
  fieldOwnerOptions = Array.from({ length: 10 }, (_, i) => ({ id: `${i + 1}`, name: `${i + 1}`, value: i + 1 }));
  // 0 means no employees, which lets Employee Payroll be $0
  employeeOptions = Array.from({ length: 21 }, (_, i) => ({ id: `${i}`, name: `${i}`, value: i }));

  constructor() {
    this.data = this.router.getCurrentNavigation()?.extras.state || {};
  }
  get classesFormArray(): FormArray {
    return this.form()?.get('classes') as FormArray;
  }

  // Once the producer changes any percentage, stop auto-populating for good
  private percentagesEdited = false;

  ngOnInit(): void {
    let selectedCount = 0;
    this.form()?.get('classes')?.valueChanges.subscribe((classes: WizardClassViewModel[]) => {
      this.checkPercentagesEdited();
      // Re-split evenly when a class is removed/cleared, or a newly selected class has no percentage yet.
      // Loading a saved endorsement adds classes that already have percentages, so those are kept.
      const selected = classes.filter((c) => c.classCode);
      const hasNewClass = selected.some((c) => c.percentageOfWork == null || `${c.percentageOfWork}` === '');
      const previousCount = selectedCount;
      selectedCount = selected.length; // update first: the split's setValue re-enters this subscription
      if (!this.percentagesEdited && selected.length !== previousCount && (selected.length < previousCount || hasNewClass)) {
        this.splitPercentageOfWork();
      }
      this.wizardStore.updateFields(this.form()?.value); // Sync with store
    });

    // Owners, employee payroll, or a percentage changed, so recalculate each class's payroll
    this.form()?.valueChanges.subscribe(() => this.calculateClassPayroll());
  }

  addClass(classData: WizardClassViewModel = { classCode: '', payroll: undefined }): void {
    const classGroup = this._fb.group({
      classCode: [classData.classCode || '', Validators.required],
      // payroll is no longer entered; it's calculated from percentageOfWork
      payroll: [classData.payroll || null],
      percentageOfWork: [classData.percentageOfWork ?? null, [Validators.required, Validators.min(1), Validators.max(100)]],
    });
    this.classesFormArray.push(classGroup);
  }

  // Remove a class from the FormArray and store
  removeClass(index: number): void {
    if (index > 0) { // don't remove the first class
      this.checkPercentagesEdited(); // before the edited row itself is removed
      this.classesFormArray.removeAt(index);
    }
  }

  // Typing marks a percentage dirty (setValue doesn't); saved endorsements with producer-entered
  // percentages are marked dirty on load by the coverage adjustment form
  private checkPercentagesEdited(): void {
    if (this.classesFormArray.controls.some((row) => row.get('percentageOfWork')?.dirty)) {
      this.percentagesEdited = true;
    }
  }

  /**
   * Evenly split 100% across the selected classes, e.g. 3 classes = 34 / 33 / 33.
   * Rows with no class selected stay blank.
   */
  private splitPercentageOfWork(): void {
    const percent = (row: AbstractControl) => row.get('percentageOfWork')!;
    const rows = this.classesFormArray.controls;
    const isSelected = (row: AbstractControl) => !!row.get('classCode')?.value;

    rows.filter((row) => !isSelected(row)).forEach((row) => percent(row).setValue(null));

    const selectedControls = rows.filter(isSelected).map(percent);
    if (!selectedControls.length) return;

    const split = evenPercentageSplit(selectedControls.length);
    selectedControls.forEach((c, i) => c.setValue(split[i]));
  }

  /**
   * The rater still takes a payroll per class, so spread the combined payroll
   * (owner payroll + employee payroll) across the classes by percentage of work.
   * Subcontractor costs are not included. Any rounding difference goes to the primary class
   * so the classes add up to the combined payroll exactly.
   */
  private calculateClassPayroll(): void {
    // Policies bound before owners/percentage of work existed have no owner count; keep their saved class payroll
    if (this.form()?.get('numberOfFieldOwners')?.value == null) return;

    // Read from the form rather than the store, which may not have synced this change yet
    const ownerPayroll = (Number(this.form()?.get('numberOfFieldOwners')?.value) || 0) * PAYROLL_PER_FIELD_OWNER;
    const employeePayroll = Number(String(this.form()?.get('employeePayroll')?.value ?? '0').replace(/[^0-9.-]+/g, '')) || 0;
    const combinedPayroll = ownerPayroll + employeePayroll;
    const rows = this.classesFormArray.controls;
    const percents = rows.map((row) => row.get('percentageOfWork')?.value);

    const payrolls = percents.map((percent) =>
      percent == null || percent === '' ? null : Math.round((combinedPayroll * Number(percent)) / 100)
    );

    const totalPercent = percents.reduce((sum, p) => sum + (Number(p) || 0), 0);
    if (totalPercent === 100 && payrolls[0] != null) {
      const assigned = payrolls.reduce((sum: number, p) => sum + (p ?? 0), 0);
      payrolls[0] += combinedPayroll - assigned;
    }

    // Only set changed values, so this doesn't retrigger itself through valueChanges
    rows.forEach((row, i) => {
      const payrollControl = row.get('payroll');
      if (payrollControl?.value !== payrolls[i]) {
        payrollControl?.setValue(payrolls[i]);
      }
    });
  }
}